import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-hot-toast";
import { httpConfigApi, httpEndpointApi } from "../services/api";
import {
  HttpRequestConfig,
  HttpRequestConfigCreate,
  HttpRequestConfigFilters,
  HttpRequestConfigUpdate,
  HttpRequestEndpoint,
  HttpRequestEndpointCreate,
  HttpRequestEndpointFilters,
  HttpRequestEndpointUpdate,
} from "../types/http";

export const useHttpConfigs = (filters: HttpRequestConfigFilters = {}) => {
  return useQuery(
    ["http-configs", filters],
    () => httpConfigApi.getConfigs(filters),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useHttpConfig = (id: number) => {
  return useQuery(["http-config", id], () => httpConfigApi.getConfig(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateHttpConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: HttpRequestConfigCreate) => httpConfigApi.createConfig(data),
    {
      onSuccess: (created: HttpRequestConfig) => {
        queryClient.invalidateQueries(["http-configs"]);
        toast.success(`Config "${created.name}" created successfully`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to create config";
        toast.error(message);
      },
    },
  );
};

export const useUpdateHttpConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: number; data: HttpRequestConfigUpdate }) =>
      httpConfigApi.updateConfig(id, data),
    {
      onSuccess: (updated: HttpRequestConfig) => {
        queryClient.setQueryData(["http-config", updated.id], updated);
        queryClient.invalidateQueries(["http-configs"]);
        toast.success(`Config "${updated.name}" updated successfully`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to update config";
        toast.error(message);
      },
    },
  );
};

export const useDeleteHttpConfig = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => httpConfigApi.deleteConfig(id), {
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries(["http-config", deletedId]);
      queryClient.invalidateQueries(["http-configs"]);
      toast.success("Config deleted successfully");
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || "Failed to delete config";
      toast.error(message);
    },
  });
};

export const useHttpEndpoints = (filters: HttpRequestEndpointFilters = {}) => {
  return useQuery(
    ["http-endpoints", filters],
    () => httpEndpointApi.getEndpoints(filters),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useHttpEndpoint = (id: number) => {
  return useQuery(
    ["http-endpoint", id],
    () => httpEndpointApi.getEndpoint(id),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useCreateHttpEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: HttpRequestEndpointCreate) => httpEndpointApi.createEndpoint(data),
    {
      onSuccess: (created: HttpRequestEndpoint) => {
        queryClient.invalidateQueries(["http-endpoints"]);
        toast.success(`Endpoint "${created.name}" created successfully`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to create endpoint";
        toast.error(message);
      },
    },
  );
};

export const useUpdateHttpEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: number; data: HttpRequestEndpointUpdate }) =>
      httpEndpointApi.updateEndpoint(id, data),
    {
      onSuccess: (updated: HttpRequestEndpoint) => {
        queryClient.setQueryData(["http-endpoint", updated.id], updated);
        queryClient.invalidateQueries(["http-endpoints"]);
        toast.success(`Endpoint "${updated.name}" updated successfully`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to update endpoint";
        toast.error(message);
      },
    },
  );
};

export const useDeleteHttpEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => httpEndpointApi.deleteEndpoint(id), {
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries(["http-endpoint", deletedId]);
      queryClient.invalidateQueries(["http-endpoints"]);
      toast.success("Endpoint deleted successfully");
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || "Failed to delete endpoint";
      toast.error(message);
    },
  });
};
